import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { isUsernameTaken, createUserProfile } from '../services/firebase/users';
import { logError } from '../utils/logger';
import { isProfane } from '../utils/profanity';

const USERNAME_REGEX = /^[a-zA-Z0-9_åäöÅÄÖæøÆØ]{3,20}$/;

export default function UsernameSetupPage() {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [consented, setConsented] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!USERNAME_REGEX.test(username)) {
      setError(t('setup.errors.invalidFormat'));
      return;
    }

    if (isProfane(username)) {
      setError(t('setup.errors.profanity'));
      return;
    }

    if (!consented) {
      setError(t('setup.errors.noConsent'));
      return;
    }

    setSubmitting(true);
    try {
      const taken = await isUsernameTaken(username);
      if (taken) {
        setError(t('setup.errors.taken'));
        return;
      }

      await createUserProfile(user.uid, {
        username,
        displayName: user.displayName || username,
      });

      await refreshProfile();
      navigate('/lobby', { replace: true });
    } catch (err) {
      setError(t('setup.errors.generic'));
      logError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen t-bg-page flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-4xl mb-3" aria-hidden="true">👤</div>
          <h1 className="text-2xl font-bold t-text mb-1">{t('setup.chooseUsername')}</h1>
          <p className="t-text-muted text-sm">{t('setup.subtitle')}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="t-bg-surface t-border border rounded-2xl p-8 space-y-5"
        >
          <div>
            <label htmlFor="username" className="block text-sm font-medium t-text-secondary mb-2">
              {t('setup.usernameLabel')}
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('setup.usernamePlaceholder')}
              maxLength={20}
              autoComplete="off"
              aria-describedby={error ? 'username-setup-error' : undefined}
              className="w-full t-bg-input t-border-input border rounded-lg px-4 py-3 t-text t-placeholder t-focus-input"
            />
            <p className="mt-1.5 text-xs t-text-muted">
              {t('setup.usernameHint')}
            </p>
            <p className="mt-1 text-xs t-text-warning">
              {t('setup.usernameVisibleWarning')}
            </p>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="privacy-consent"
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              required
              className="mt-0.5 h-4 w-4 shrink-0 rounded t-border-input border t-bg-input accent-(--c-action-bg) cursor-pointer t-focus-ring"
            />
            <label htmlFor="privacy-consent" className="text-xs t-text-muted leading-relaxed cursor-pointer">
              {t('setup.privacyConsentPre')}{' '}
              <Link
                to="/privacy"
                target="_blank"
                className="t-text-accent underline hover:t-text t-focus-ring rounded"
              >
                {t('setup.privacyPolicy')}
              </Link>
              {t('setup.privacyConsentPost')}
            </label>
          </div>

          {error && (
            <p id="username-setup-error" role="alert" className="text-sm t-text-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !username || !consented}
            className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
          >
            {submitting ? t('setup.saving') : t('setup.getStarted')}
          </button>
        </form>
      </div>
    </div>
  );
}
